import apiClient from '@/api/client';
import type { Conversation, Message, CreateConversationRequest, SendMessageRequest, UpdateConversationRequest, LlmModel, UsageSummary } from '../types';
import { postEventStream, type SSESource } from './sse';

export async function createConversation(request: CreateConversationRequest): Promise<Conversation> {
  const response = await apiClient.post('/assistant/conversations', request);
  return response.data;
}

export async function getConversations(): Promise<Conversation[]> {
  const response = await apiClient.get('/assistant/conversations');
  return response.data;
}

export async function getConversation(id: string): Promise<{ conversation: Conversation; messages: Message[] }> {
  const response = await apiClient.get(`/assistant/conversations/${id}`);
  return response.data;
}

export async function deleteConversation(id: string): Promise<void> {
  await apiClient.delete(`/assistant/conversations/${id}`);
}

/** Deletes a previously-asked question and its response (the backend removes both as one turn). */
export async function deleteMessage(conversationId: string, messageId: string): Promise<void> {
  await apiClient.delete(`/assistant/conversations/${conversationId}/messages/${messageId}`);
}

/**
 * Send a message and receive the assistant's reply as an SSE stream (message/tool_call/
 * action_confirmation/done/error events — see the SSE Event Contract in ../types).
 * The backend endpoint is SSE-only, so this must be consumed as a stream, not a plain POST
 * (a plain POST silently discards the events — including action_confirmation, which is how a
 * WRITE tool's first pause in a conversation used to render as a blank/empty message).
 */
export function sendMessageStream(conversationId: string, request: SendMessageRequest): SSESource {
  return postEventStream(`/assistant/conversations/${conversationId}/messages`, request);
}

export async function updateConversation(id: string, request: UpdateConversationRequest): Promise<Conversation> {
  const response = await apiClient.patch(`/assistant/conversations/${id}`, request);
  return response.data;
}

export async function getAvailableModels(): Promise<LlmModel[]> {
  const response = await apiClient.get('/llm/models');
  return response.data;
}

/** The model a conversation uses when it doesn't have one explicitly set. */
export async function getDefaultModel(): Promise<{ modelId: string }> {
  const response = await apiClient.get('/assistant/default-model');
  return response.data;
}

/** Estimated AI assistant spend/token usage across all of the current user's conversations. */
export async function getUsageSummary(): Promise<UsageSummary> {
  const response = await apiClient.get('/assistant/usage');
  return response.data;
}

/** Confirm a pending write action and receive the continued SSE response. */
export function confirmAction(conversationId: string): SSESource {
  return postEventStream(`/assistant/conversations/${conversationId}/confirm-action`);
}

/**
 * Regenerate the response to the most recent user message in place — the backend discards the
 * previous ASSISTANT/TOOL messages for that turn and streams a fresh reply, rather than this
 * appending a whole new turn on top of the old one.
 */
export function regenerate(conversationId: string): SSESource {
  return postEventStream(`/assistant/conversations/${conversationId}/regenerate`);
}

/** Cancel a pending write action. */
export async function cancelAction(conversationId: string): Promise<void> {
  await apiClient.post(`/assistant/conversations/${conversationId}/cancel-action`);
}

/**
 * Stop an in-flight assistant turn from producing further output. Best-effort — the backend
 * can't reliably abort the underlying Bedrock call, but it stops forwarding more SSE events and
 * won't act on tool calls once cancelled. Pair with closing the SSE connection client-side for
 * an instant UI stop regardless of server timing.
 */
export async function cancelGeneration(conversationId: string): Promise<void> {
  await apiClient.post(`/assistant/conversations/${conversationId}/cancel-generation`);
}
